import type { PurchaseOrder } from '@erp/rpc';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  FileTextIcon,
  BuildingsIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  PackageIcon,
  HashIcon,
  LinkIcon,
  WarningIcon,
  NoteIcon,
  CaretDownIcon,
} from '@phosphor-icons/react';
import { Badge } from '@/components/ui/badge';
import { formatDateOnlyString } from '@/utils/dateUtils';
import { SystemEntityInline } from '@/components/system-entities';
import { SystemEntity, EntityAssociationType } from '@erp/shared-types';
import { useEntityAssociationsList } from '@/api/rpc/hooks/core/useEntityAssociations';
import { useMemo, useState } from 'react';
import { useAPInvoice } from '@/api/rpc/hooks/accounts-payable/useAPInvoices';
import { T, Var, msg, useMessages, useGT } from 'gt-react';

const PO_STATUS_LABELS: Record<string, string> = {
  DRAFT: msg('Draft', {
    $context: 'purchase order workflow status label',
  }) as string,
  PENDING_APPROVAL: msg('Pending Approval', {
    $context: 'purchase order workflow status label',
  }) as string,
  APPROVED: msg('Approved', {
    $context: 'purchase order workflow status label',
  }) as string,
  SENT: msg('Sent', {
    $context: 'purchase order workflow status label',
  }) as string,
  PARTIALLY_RECEIVED: msg('Partially Received', {
    $context: 'purchase order workflow status label',
  }) as string,
  FULLY_RECEIVED: msg('Fully Received', {
    $context: 'purchase order workflow status label',
  }) as string,
  PARTIALLY_INVOICED: msg('Partially Invoiced', {
    $context: 'purchase order workflow status label',
  }) as string,
  FULLY_INVOICED: msg('Fully Invoiced', {
    $context: 'purchase order workflow status label',
  }) as string,
  CLOSED: msg('Closed', {
    $context: 'purchase order workflow status label',
  }) as string,
  CANCELLED: msg('Cancelled', {
    $context: 'purchase order workflow status label',
  }) as string,
};

const STATUS_VARIANTS: Record<
  string,
  'default' | 'secondary' | 'outline' | 'destructive'
> = {
  DRAFT: 'outline',
  PENDING_APPROVAL: 'secondary',
  APPROVED: 'default',
  SENT: 'default',
  PARTIALLY_RECEIVED: 'secondary',
  FULLY_RECEIVED: 'default',
  PARTIALLY_INVOICED: 'secondary',
  FULLY_INVOICED: 'default',
  CLOSED: 'outline',
  CANCELLED: 'destructive',
};

const formatCurrencyValue = (
  value: string | undefined | null,
  currency: string | undefined,
  notAvailableLabel: string,
): string => {
  if (!value) return notAvailableLabel;

  const numeric = Number(value);
  if (Number.isNaN(numeric)) return value;

  return numeric.toLocaleString(undefined, {
    style: 'currency',
    currency: currency || 'USD',
    minimumFractionDigits: 2,
  });
};

interface PurchaseOrderMetadataProps {
  purchaseOrder: PurchaseOrder;
  hasPaymentHoldLines?: boolean;
}

export function PurchaseOrderMetadata({
  purchaseOrder,
  hasPaymentHoldLines,
}: PurchaseOrderMetadataProps) {
  const gt = useGT();
  const m = useMessages();

  const notAvailableLabel = gt('N/A', {
    $context: 'placeholder when a currency or numeric value is not available',
  });

  const statusLabelEncoded =
    PO_STATUS_LABELS[purchaseOrder.status as keyof typeof PO_STATUS_LABELS];
  const statusLabel = statusLabelEncoded
    ? m(statusLabelEncoded)
    : purchaseOrder.status;

  // Fetch associated invoices
  const invoiceAssociationsQuery = useEntityAssociationsList({
    entityId: purchaseOrder.id,
    entityType: SystemEntity.INTERNAL_PURCHASE_ORDER,
    associationType: EntityAssociationType.AP_INVOICE_MATCHING,
  });

  const invoiceAssociations = useMemo(
    () => invoiceAssociationsQuery.data?.associations || [],
    [invoiceAssociationsQuery.data?.associations],
  );

  // Filter to only get invoice-level associations (not line items)
  const linkedInvoices = useMemo(
    () =>
      invoiceAssociations.filter(
        (assoc) => assoc.entity.entityType === SystemEntity.AP_INVOICE,
      ),
    [invoiceAssociations],
  );

  // Get the first linked invoice to show its date
  const firstLinkedInvoiceId = linkedInvoices[0]?.entity.entityId;
  const { invoice: firstLinkedInvoice } = useAPInvoice(
    firstLinkedInvoiceId,
    !!firstLinkedInvoiceId,
  );

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base">
            <FileTextIcon className="h-4 w-4" />
            {purchaseOrder.externalId || (
              <T context="shown in PO header when no external PO number exists">
                No PO Number
              </T>
            )}
          </CardTitle>
          <Badge variant={STATUS_VARIANTS[purchaseOrder.status] || 'outline'}>
            {statusLabel}
          </Badge>
        </div>
      </CardHeader>

      <CardContent className="space-y-3">
        {hasPaymentHoldLines && (
          <div className="flex items-center gap-2 text-amber-600 bg-amber-50 border border-amber-200 rounded-md p-2">
            <WarningIcon className="h-4 w-4" />
            <span className="text-sm">
              <T context="accounts payable purchase order: some lines blocked from payment">
                Some line items are on payment hold
              </T>
            </span>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-0.5">
            <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
              <HashIcon className="h-3 w-3" />
              <T context="label for internal purchase order identifier">
                Internal ID
              </T>
            </div>
            <div className="font-medium text-xs font-mono">
              {purchaseOrder.internalId}
            </div>
          </div>

          <div className="space-y-0.5">
            <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
              <BuildingsIcon className="h-3 w-3" />
              <T context="label for supplier on purchase order detail">
                Supplier
              </T>
            </div>
            <div className="flex items-start">
              <SystemEntityInline
                entityType={SystemEntity.AP_SUPPLIER}
                entityId={purchaseOrder.supplierId}
              />
            </div>
          </div>

          {/* Linked Invoices */}
          {linkedInvoices.length > 0 && (
            <div className="space-y-0.5 col-span-2">
              <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                <LinkIcon className="h-3 w-3" />
                {linkedInvoices.length > 1 ? (
                  <T context="label when multiple invoices are linked to the PO">
                    Linked Invoices
                  </T>
                ) : (
                  <T context="label when one invoice is linked to the PO">
                    Linked Invoice
                  </T>
                )}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {linkedInvoices.map((invoiceAssoc) => (
                  <SystemEntityInline
                    key={invoiceAssoc.entity.entityId}
                    entityType={invoiceAssoc.entity.entityType}
                    entityId={invoiceAssoc.entity.entityId}
                    className="text-xs"
                  />
                ))}
              </div>
            </div>
          )}

          <div className="space-y-0.5">
            <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
              <CurrencyDollarIcon className="h-3 w-3" />
              <T context="label for purchase order total amount">
                Total Amount
              </T>
            </div>
            <div className="text-lg font-bold text-green-600">
              {formatCurrencyValue(
                purchaseOrder.totalAmount,
                purchaseOrder.currency || undefined,
                notAvailableLabel,
              )}
            </div>
          </div>

          <div className="space-y-0.5">
            <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
              <PackageIcon className="h-3 w-3" />
              <T context="label for whether goods were received on the PO">
                Received
              </T>
            </div>
            <div className="font-medium text-sm">
              {purchaseOrder.receivedFlag ? (
                <T context="boolean: goods have been received">Yes</T>
              ) : (
                <T context="boolean: goods have not been received">No</T>
              )}
            </div>
          </div>

          <div className="space-y-0.5">
            <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
              <CalendarIcon className="h-3 w-3" />
              <T context="label for purchase order date">Order Date</T>
            </div>
            <div className="font-medium text-sm">
              {formatDateOnlyString(purchaseOrder.orderDate)}
            </div>
          </div>

          <div className="space-y-0.5">
            <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
              <CalendarIcon className="h-3 w-3" />
              {firstLinkedInvoice?.invoice?.invoiceDate ? (
                <T context="label for linked AP invoice date on PO detail">
                  Invoice Date
                </T>
              ) : (
                <T context="label for expected delivery date on PO detail">
                  Delivery Date
                </T>
              )}
            </div>
            <div className="font-medium text-sm">
              {firstLinkedInvoice?.invoice?.invoiceDate
                ? formatDateOnlyString(firstLinkedInvoice.invoice.invoiceDate)
                : purchaseOrder.deliveryDate
                  ? formatDateOnlyString(purchaseOrder.deliveryDate)
                  : gt('Not specified', {
                      $context:
                        'purchase order: no delivery or invoice date available',
                    })}
            </div>
          </div>
        </div>

        {/* Additional Details */}
        {(purchaseOrder.referenceId ||
          purchaseOrder.salesOrderId ||
          purchaseOrder.originSystem) && (
          <div className="border-t pt-3 space-y-2">
            {purchaseOrder.referenceId && (
              <div className="space-y-0.5">
                <div className="text-xs font-medium text-muted-foreground">
                  <T context="label for external reference identifier on PO">
                    Reference ID
                  </T>
                </div>
                <div className="text-xs font-mono">
                  {purchaseOrder.referenceId}
                </div>
              </div>
            )}
            {purchaseOrder.salesOrderId && (
              <div className="space-y-0.5">
                <div className="text-xs font-medium text-muted-foreground">
                  <T context="label for linked sales order identifier on PO">
                    Sales Order ID
                  </T>
                </div>
                <div className="text-xs font-mono">
                  {purchaseOrder.salesOrderId}
                </div>
              </div>
            )}
            {purchaseOrder.originSystem && (
              <div className="space-y-0.5">
                <div className="text-xs font-medium text-muted-foreground">
                  <T context="label for system where the PO originated">
                    Origin System
                  </T>
                </div>
                <div className="text-xs">{purchaseOrder.originSystem}</div>
              </div>
            )}
          </div>
        )}

        {/* PDF Link */}
        {purchaseOrder.pdfS3Url && (
          <div className="border-t pt-3">
            <a
              href={purchaseOrder.pdfS3Url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-blue-600 hover:underline flex items-center gap-1"
            >
              <FileTextIcon className="h-3 w-3" />
              <T context="link to open purchase order PDF in new tab">
                View PDF
              </T>
            </a>
          </div>
        )}

        {/* Famous Notes */}
        {purchaseOrder.notes && purchaseOrder.notes.length > 0 && (
          <NotesSection notes={purchaseOrder.notes} />
        )}
      </CardContent>
    </Card>
  );
}

function NotesSection({ notes }: { notes: string[] }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border-t pt-3">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground w-full"
      >
        <NoteIcon className="h-3 w-3" />
        <T context="expandable section header showing Famous notes count">
          Notes (<Var>{notes.length}</Var>)
        </T>
        <CaretDownIcon
          className={`h-3 w-3 ml-auto transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>
      {isOpen && (
        <ul className="mt-2 space-y-1.5">
          {notes.map(function renderNote(note, index) {
            return (
              <li
                key={index}
                className="text-xs text-muted-foreground bg-muted/50 rounded px-2 py-1.5"
              >
                {note}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
